import { endOfDay, startOfDay } from "date-fns";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, type Validator, reduxForm } from "redux-form";

import { CalloutIntent } from "widgets";

import { simpleDateValidation } from "../../../app-utils/form.js";
import { type Project } from "../../../graphql.js";
import { downloadSystemAuditReport } from "../../../redux/modules/data-export.js";
import { type IAppState } from "../../../redux/reducer.js";
import { Dialog } from "../overlays/Dialogs.js";
import { ReactDateSelectReduxFormField } from "../redux-form/ReactDateSelectorField.js";

interface IAuditReportFormOwnProps {
  onClose: () => void;
}

interface IAuditReportFormAppStateProps {
  //notificationsState: INotificationsState;
}

interface IAuditReportFormDispatchProps {
  downloadAuditReport: (startDate: string, endDate: string) => Promise<any>;
}

export interface IAuditReportFormProps
  extends IAuditReportFormAppStateProps,
    IAuditReportFormDispatchProps,
    IAuditReportFormOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(_state: IAppState, _ownProps: IAuditReportFormOwnProps): IAuditReportFormAppStateProps {
  return {
    initialValues: {
      [AuditReportFormImpl.AUDIT_REPORT_FIELD_START_DATE]: startOfDay(new Date()).toISOString(),
      [AuditReportFormImpl.AUDIT_REPORT_FIELD_END_DATE]: endOfDay(new Date()).toISOString(),
    },
  };
}

function mapDispatchToProps(dispatch): IAuditReportFormDispatchProps {
  return {
    downloadAuditReport: (startDate: string, endDate: string) =>
      dispatch(downloadSystemAuditReport(startDate, endDate)),
  };
}

/**
 * Button that allows to push a selection of samples (or full group) to the analytical viewer.
 *
 */
class AuditReportFormImpl extends PureComponent<IAuditReportFormProps> {
  public static AUDIT_REPORT_FORM_NAME = "downloadAuditReport";
  public static AUDIT_REPORT_FIELD_START_DATE = "startDate";
  public static AUDIT_REPORT_FIELD_END_DATE = "endDate";

  constructor(props: IAuditReportFormProps) {
    super(props);
  }

  handleSubmitData = (values): Promise<Project> => {
    const startDate = values[AuditReportFormImpl.AUDIT_REPORT_FIELD_START_DATE];
    const endDate = values[AuditReportFormImpl.AUDIT_REPORT_FIELD_END_DATE];

    return this.props.downloadAuditReport(startDate, endDate).then(
      (fulfilled) => {
        this.props.onClose();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  startDateValidation: Validator = (value: any, allValues?: any, _props?: any) => {
    const simpleValidation: string = simpleDateValidation(value);
    if (simpleValidation) {
      return simpleValidation;
    }

    const endDate: string = allValues[AuditReportFormImpl.AUDIT_REPORT_FIELD_END_DATE];
    if (endDate && new Date(endDate) < new Date(value)) {
      return "Start date must be BEFORE End date";
    }
    return undefined;
  };

  endDateValidation: Validator = (value: any, allValues?: any, _props?: any) => {
    const simpleValidation: string = simpleDateValidation(value);
    if (simpleValidation) {
      return simpleValidation;
    }

    const startDate: string = allValues[AuditReportFormImpl.AUDIT_REPORT_FIELD_START_DATE];
    if (startDate && new Date(startDate) > new Date(value)) {
      return "Start date must be BEFORE End date";
    }
    return undefined;
  };

  render() {
    return (
      <Dialog
        headerProps={{ title: "Download an Audit Report" }}
        mainActionButtonProps={{
          label: "Download",
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        closeFn={() => {
          this.props.reset();
          this.props.onClose();
        }}
        footerMessage={{ message: this.props.error, intent: CalloutIntent.Danger }}
      >
        <div>
          <p>An audit report will be generated for the dates specified and downloaded to your computer.</p>

          <div>
            <ReactDateSelectReduxFormField
              horizontalLayout={false}
              key={`audit-report-start-date`}
              name={AuditReportFormImpl.AUDIT_REPORT_FIELD_START_DATE}
              title={"Start date"}
              withTime={true}
              placeholder="Select the audit report start date/time..."
              validate={this.startDateValidation}
            />
            <ReactDateSelectReduxFormField
              horizontalLayout={false}
              key={`audit-report-end-date`}
              name={AuditReportFormImpl.AUDIT_REPORT_FIELD_END_DATE}
              title={"End date"}
              withTime={true}
              placeholder="Select the audit report end date/time..."
              validate={this.endDateValidation}
            />
          </div>
        </div>
      </Dialog>
    );
  }
}

const AuditReportForm = connect<IAuditReportFormAppStateProps, IAuditReportFormDispatchProps, IAuditReportFormOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: AuditReportFormImpl.AUDIT_REPORT_FORM_NAME,
  })(AuditReportFormImpl) as any,
);

export { AuditReportForm };
export default AuditReportForm;
